/* eslint-disable react-hooks/exhaustive-deps */

import { ComboboxRoot } from '@base-ui-components/react/combobox';
import * as Api from '@ViewModels';
import { css, StyleSheet } from 'aphrodite';
import { observer } from 'mobx-react';
import * as React from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { toast } from 'sonner';
import { getTextMessageCapablePhoneNumbers } from '../../../../../extViewmodels/Utils';
import { useLocalStorageState } from '../../../../../hooks/useLocalStorage';
import { useTextMessaging, useUserSession } from '../../../../../models/hooks/appStateHooks';
import { getDisplayName } from '../../../../../models/NameUtils';
import { getFormattedPhoneNumber } from '../../../../../models/utils';
import { useContactAutocompleteSuspenseQuery, useContactsSuspenseQuery } from '../../../../../queries';
import { Button } from '../../../../components-v2/Button';
import {
	Combobox,
	ComboboxChip,
	ComboboxChipRemove,
	ComboboxChips,
	ComboboxChipsInput,
	ComboboxCollection,
	ComboboxContent,
	ComboboxGroup,
	ComboboxGroupLabel,
	ComboboxItem,
	ComboboxList,
	ComboboxStatus,
	useComboboxAnchor,
} from '../../../../components-v2/Combobox';
import { errorLight, tagBackgroundColor } from '../../../../styles/colors';
import { bs } from '../../../../styles/styles';
import { Avatar2 } from '../../../Avatar2';
import { MessageSquareIcon } from '../../../svgs/icons/MessageSquare';
import { ConversationContactEditor } from '../../ConversationContactEditor';
import { ContactError } from '../../ContactError';
import { useTextMessageConversationsContext } from '../context';
import { styleSheet } from '../styles';
import { verifyIsNotAlreadyAdded } from './utils';
import { QueryErrorResetBoundary } from '@tanstack/react-query';

interface IProps {
	missingNumbers: Api.ITextRecipient[];
}

const MAX_RECIPIENTS = 9;

interface ISelectedRecipientPill {
	id: string;
	needsResolved?: boolean;
	value: string;
}

const getRecentSearchLocalStorageKey = (userId: string) => `recent-people-searches-${userId}`;

export const NewConversation: React.FC<IProps> = observer(({ missingNumbers }) => {
	const textMessagingVM = useTextMessaging();
	const {
		recipients,
		selectedConversation,
		selectedRecipient,
		setRecipients,
		setShowNewConversationSearch,
		showNewConversationSearch,
	} = useTextMessageConversationsContext();
	const userSession = useUserSession();
	const anchor = useComboboxAnchor();
	const [input, setInput] = React.useState('');
	const [recentSearches, setRecentSearches] = useLocalStorageState<string[]>(
		getRecentSearchLocalStorageKey(userSession.user?.id),
		[]
	);
	const onContactClick = (contact: Api.IContact) => {
		onRecipientSelected(contact);
		setInput('');
		const recentSearchSet = new Set([contact.id, ...recentSearches.slice(-10)]);
		setRecentSearches(Array.from(recentSearchSet));
	};
	const onInputChange = (value: string, eventDetails: ComboboxRoot.ChangeEventDetails) => {
		// if escape key is pressed, call onCancel
		if ((eventDetails.event as KeyboardEvent).key === 'Escape') {
			onCancelNewConversationClick();
			return;
		}

		setInput(value);
	};

	const onCancelNewConversationClick = () => {
		setShowNewConversationSearch(false);
		setRecipients([]);
	};

	const alreadyAddedToasterError = () => {
		toast.error('Recipient has already been added to the conversation.');
	};

	const onRecipientSelected = async (contact: Api.IContact) => {
		const recipient = new Api.TextRecipientViewModel(userSession, contact);
		const alreadyAdded = verifyIsNotAlreadyAdded(
			recipient,
			recipients as Api.TextRecipientViewModel[],
			alreadyAddedToasterError
		);
		if (!alreadyAdded) {
			const newRecipients = [...recipients, recipient];
			setRecipients(newRecipients);
		}
	};

	const onRemoveRecipient = (recipient: ISelectedRecipientPill) => {
		const newRecipients = recipients.filter(r => {
			if (r instanceof Api.TextRecipientViewModel) {
				return r.id !== recipient.id;
			}
			const p = r as Api.IPhoneNumber;
			const phone = p.metadata?.standard ?? p.value;
			return phone !== recipient.id;
		});
		setRecipients(newRecipients);
	};

	const onUnknownNumberSelected = (num: string) => {
		const alreadyAdded = verifyIsNotAlreadyAdded(
			{ value: num },
			recipients as Api.TextRecipientViewModel[],
			alreadyAddedToasterError
		);
		if (!alreadyAdded) {
			const phoneNumber: Api.IPhoneNumber = { value: num };
			const newRecipients = [...recipients, phoneNumber];
			setRecipients(newRecipients);
		}
	};

	const getInvalidContactString = () => {
		let result = '';
		if (missingNumbers?.length > 0) {
			for (let i = 0; i < missingNumbers.length; i++) {
				const num = missingNumbers[i];
				const displayName = getDisplayName(num.contact) || `unknown contact number ${num.number.standard}`;
				result += i === 0 ? displayName : `, ${displayName}`;
			}
		}
		return result;
	};

	const getRecipientsNames = () => {
		return recipients.map(r => {
			if (r instanceof Api.TextRecipientViewModel) {
				let value = 'unknown';
				if (r.firstName) {
					value = r.firstName;
				} else if (r.lastName) {
					value = r.lastName;
				} else if (r.selectedPhoneNumber) {
					value = r.selectedPhoneNumber.metadata?.standard ?? r.selectedPhoneNumber.value;
				} else if (r.primaryEmail?.value) {
					value = r.primaryEmail.value;
				}

				const recipient: ISelectedRecipientPill = { id: r.id, value };
				if (r.needsResolving) {
					recipient.needsResolved = true;
				}
				return recipient;
			}
			const p = r as Api.IPhoneNumber;
			const phone = p.metadata?.standard ?? p.value;
			return { id: phone, value: phone };
		});
	};

	const showStarNewConversationMessage = !showNewConversationSearch && !selectedConversation && recipients.length === 0;

	const showSelectedConversation = !showNewConversationSearch && !!selectedConversation;
	const selectedRecipients = getRecipientsNames();

	const disableSearch =
		(selectedRecipients?.length > 0 && !textMessagingVM.supportsGroupTexting) ||
		recipients.length >= MAX_RECIPIENTS ||
		!!selectedRecipient?.needsResolving;

	return (
		<div
			className={css(
				styleSheet.conversationHeader,
				showStarNewConversationMessage ? styleSheet.conversationHeaderLeft : null
			)}
		>
			{showNewConversationSearch ? (
				<div className={css(s.root)}>
					<Combobox multiple inputValue={input} onInputValueChange={onInputChange}>
						<ComboboxChips ref={anchor} styles={[s.combobox]}>
							{selectedRecipients?.length
								? selectedRecipients.map(r => {
										return (
											<ComboboxChip key={r.id} styles={[s.recipientPill, r.needsResolved && s.needsResolved]}>
												{r.value}
												<ComboboxChipRemove onClick={() => onRemoveRecipient(r)} />
											</ComboboxChip>
										);
									})
								: null}
							<ComboboxChipsInput
								styles={[s.comboboxInput]}
								placeholder={selectedRecipients?.length ? 'Add contacts' : 'To:'}
								value={input}
								autoFocus
								disabled={disableSearch}
							/>
						</ComboboxChips>
						<ComboboxContent anchor={anchor} styles={[s.comboboxContent]}>
							<QueryErrorResetBoundary>
								{({ reset }) => (
									<ErrorBoundary
										resetKeys={[input]}
										onReset={reset}
										fallbackRender={({ resetErrorBoundary }) => (
											<ComboboxStatus styles={[s.errorStatus]}>
												<span>Couldn't load contacts.</span>
												<Button variant='ghost' size='sm' onClick={resetErrorBoundary}>
													Retry
												</Button>
											</ComboboxStatus>
										)}
									>
										{!input ? (
											<React.Suspense fallback={<ComboboxStatus>Loading recent searches...</ComboboxStatus>}>
												<RecentSearchesComboboxGroup onClick={onContactClick} />
											</React.Suspense>
										) : (
											<React.Suspense fallback={<ComboboxStatus>Loading contacts...</ComboboxStatus>}>
												<ContactAutocompleteComboboxGroup
													search={input}
													onClick={onContactClick}
													onUnknownNumberSelected={onUnknownNumberSelected}
												/>
											</React.Suspense>
										)}
									</ErrorBoundary>
								)}
							</QueryErrorResetBoundary>
						</ComboboxContent>
					</Combobox>
					<Button variant='link' onClick={onCancelNewConversationClick}>
						Cancel
					</Button>
				</div>
			) : null}
			{showSelectedConversation &&
				(selectedConversation?.toNumbers?.length === 1 ? (
					<ConversationContactEditor
						key={selectedConversation.id}
						conversation={selectedConversation}
						onContactChanged={() => {
							selectedConversation.load?.();
						}}
					/>
				) : (
					<div className={css(styleSheet.conversationHeaderContentContainer)}>
						<p className={css(styleSheet.conversationHeaderNames)}>
							{Api.VmUtils.renderConversationNames(selectedConversation)}
						</p>
					</div>
				))}
			{missingNumbers.length > 0 && (
				<ContactError
					className={css(styleSheet.alert)}
					text={
						selectedConversation?.toNumbers?.length > 1
							? `The following recipients in this conversation are no longer up to date: ${getInvalidContactString()}`
							: 'This recipient is no longer up to date.'
					}
				/>
			)}
		</div>
	);
});

function ContactAutocompleteComboboxGroup({
	search,
	onClick,
	onUnknownNumberSelected,
}: {
	search: string;
	onClick: (contact: Api.IContact) => void;
	onUnknownNumberSelected?: (phoneNumber: string) => void;
}) {
	const { data } = useContactAutocompleteSuspenseQuery({
		fragment: search,
		pageSize: 10,
		searchFields: 'nameParts',
	});
	const id = React.useId();
	const unknownNumberMatch = search.match(/\d+/g)?.join('');
	const contacts = data.pages.flatMap(page => page.values);
	if (contacts.length === 0) {
		if (unknownNumberMatch?.length === 10) {
			return (
				<ComboboxList>
					<ComboboxItem onClick={() => onUnknownNumberSelected?.(unknownNumberMatch)}>
						<MessageSquareIcon width={16} height={16} /> Send text to {getFormattedPhoneNumber(unknownNumberMatch)}
					</ComboboxItem>
				</ComboboxList>
			);
		}
		return <ComboboxStatus>Enter a valid 10 digit phone number</ComboboxStatus>;
	}
	return (
		<ComboboxList>
			<ComboboxGroup key={`${id}-contact-searches`} items={contacts}>
				<ComboboxGroupLabel>Contacts</ComboboxGroupLabel>
				<ComboboxCollection>
					{(contact: Api.IContact) => (
						<RecipientContactComboboxItem contact={contact} onClick={() => onClick(contact)} />
					)}
				</ComboboxCollection>
			</ComboboxGroup>
		</ComboboxList>
	);
}

interface RecipientContactComboboxItemProps extends React.ComponentProps<typeof ComboboxItem> {
	contact: Api.IContact;
}

function RecipientContactComboboxItem({ contact, ...rest }: RecipientContactComboboxItemProps) {
	const phoneNumberToShow = (getTextMessageCapablePhoneNumbers(contact.phoneNumbers)?.[0] ?? contact.phoneNumbers?.[0])
		?.metadata?.standard;

	return (
		<ComboboxItem value={contact} {...rest}>
			<div className={css(bs.flex, bs.itemsCenter, bs.gap2, bs.truncateText)}>
				<Avatar2
					imgSrc={contact.profilePic}
					fallbackText={contact.firstName?.substring(0, 1) + contact.lastName?.substring(0, 1)}
					styles={[bs.w10, bs.h10]}
				/>
				<div className={css(bs.flex1, bs.minW0)}>
					<div className={css(bs.fontBold, bs.truncateText)}>{getDisplayName(contact)}</div>
					<div
						className={css(
							bs.textSm,
							bs.textDarkGrayFontColor,
							bs.truncateText,
							!phoneNumberToShow && bs.textDestructive
						)}
					>
						{phoneNumberToShow ?? 'No number'}
					</div>
				</div>
			</div>
		</ComboboxItem>
	);
}

function RecentSearchesComboboxGroup({ onClick }: { onClick: (contact: Api.IContact) => void }) {
	const userSession = useUserSession();
	const [recentSearches] = useLocalStorageState<string[]>(getRecentSearchLocalStorageKey(userSession.user?.id), []);
	const queries = useContactsSuspenseQuery({
		contactIds: recentSearches,
	});
	const contacts = queries.map(query => query.data);
	if (contacts.length === 0) {
		return <ComboboxStatus>No recent searches</ComboboxStatus>;
	}
	return (
		<ComboboxList>
			<ComboboxGroup key='recent-people-searches' items={contacts}>
				<ComboboxGroupLabel>Recent Searches</ComboboxGroupLabel>
				<ComboboxCollection>
					{item => <RecipientContactComboboxItem contact={item} onClick={() => onClick(item)} />}
				</ComboboxCollection>
			</ComboboxGroup>
		</ComboboxList>
	);
}

const s = StyleSheet.create({
	root: {
		display: 'flex',
		alignItems: 'center',
		gap: 8,
		width: '100%',
	},
	combobox: {
		flexGrow: 1,
		border: 0,
		height: '100%',
	},
	comboboxInput: {
		height: '100%',
	},
	recipientPill: {
		background: tagBackgroundColor,
		borderRadius: 30,
		fontWeight: 'normal',
	},
	needsResolved: {
		background: errorLight,
	},
	comboboxContent: {
		minWidth: `320px !important`,
		width: 320,
	},
	errorStatus: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'space-between',
		gap: 8,
	},
});
